import { useEffect, useState } from "react";
import { GitBranch, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getGhlPipelines, getGhlPipelineSync, saveGhlPipelineSync, previewGhlPipelineSync } from "@/lib/api";

// Settings → GoHighLevel pipeline sync: which pipeline/stages a consult's outcome moves to.
const Select = ({ label, value, onChange, options, placeholder = "— don't move —", testid }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">{label}</div>
    <select value={value || ""} onChange={(e) => onChange(e.target.value || null)} data-testid={testid}
      className="w-full rounded-md bg-[#0a0a0a] border border-[#262626] h-10 px-2 text-sm text-zinc-100">
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
  </label>
);

export default function GhlPipelineSyncCard({ canEdit }) {
  const [cfg, setCfg] = useState(null);
  const [pipes, setPipes] = useState(null);
  const [loadingPipes, setLoadingPipes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const check = async () => {
    setChecking(true);
    try { setPreview(await previewGhlPipelineSync(cfg.pipeline_id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't check"); }
    finally { setChecking(false); }
  };

  useEffect(() => { getGhlPipelineSync().then(setCfg).catch(() => setCfg({ enabled: false })); }, []);
  const loadPipes = async () => {
    setLoadingPipes(true);
    try { setPipes((await getGhlPipelines()).pipelines); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load GoHighLevel pipelines"); }
    finally { setLoadingPipes(false); }
  };
  useEffect(() => { if (canEdit) loadPipes(); }, [canEdit]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!cfg) return null;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const pipe = (pipes || []).find((p) => p.id === cfg.pipeline_id);
  const stages = pipe?.stages || [];

  const save = async (enabled = cfg.enabled) => {
    setSaving(true);
    try {
      const names = Object.fromEntries(stages.map((s) => [s.id, s.name]));
      const saved = await saveGhlPipelineSync({ ...cfg, enabled, pipeline_name: pipe?.name || cfg.pipeline_name, stage_names: names });
      setCfg(saved);
      toast.success(enabled ? "GoHighLevel sync is on for this gym" : "Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };

  return (
    <section className="card-surface p-6 space-y-4" data-testid="ghl-pipeline-sync">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2"><GitBranch className="h-4 w-4 text-zinc-400" /><h3 className="font-semibold">GoHighLevel pipeline sync</h3></div>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            When a consult is saved as Sold or Not sold, FPA finds the person in this gym&apos;s GoHighLevel (by phone, then email)
            and moves their opportunity to the stage you choose here.
          </p>
        </div>
        <span className={`rounded-full border px-2.5 py-0.5 text-xs ${cfg.enabled ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-[#333] text-zinc-400"}`}>
          {cfg.enabled ? "On" : "Off"}
        </span>
      </div>
      {!canEdit ? (
        <p className="text-sm text-zinc-500">{cfg.enabled ? `Moving consults in "${cfg.pipeline_name}".` : "Not set up."} Managers can change this.</p>
      ) : !pipes ? (
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          {loadingPipes ? <><Loader2 className="h-4 w-4 animate-spin" />Loading pipelines from GoHighLevel…</> : <Button size="sm" variant="outline" onClick={loadPipes} className="border-[#262626]">Load pipelines</Button>}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select label="Pipeline" value={cfg.pipeline_id} onChange={(v) => setCfg((c) => ({ ...c, pipeline_id: v, stage_sold: null, stage_pending: null, stage_not_sold: null, stage_refunded: null }))}
              options={pipes} placeholder="Choose the consults pipeline" testid="ghl-pipeline" />
            <div />
            <Select label="Sold → stage" value={cfg.stage_sold} onChange={(v) => set("stage_sold", v)} options={stages} testid="ghl-stage-sold" />
            <Select label="Sold · pending payment → stage" value={cfg.stage_pending} onChange={(v) => set("stage_pending", v)} options={stages}
              placeholder="— same as Sold —" testid="ghl-stage-pending" />
            <Select label="Not sold → stage" value={cfg.stage_not_sold} onChange={(v) => set("stage_not_sold", v)} options={stages} testid="ghl-stage-not-sold" />
            <Select label="Refunded → stage (reopens the opportunity)" value={cfg.stage_refunded} onChange={(v) => set("stage_refunded", v)} options={stages} testid="ghl-stage-refunded" />
            <Select label="Pre-sale questionnaire submitted → stage" value={cfg.stage_showed} onChange={(v) => set("stage_showed", v)} options={stages}
              placeholder="— the stage named “Showed” —" testid="ghl-stage-showed" />
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-300">
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={cfg.won_on_sold !== false} onChange={(e) => set("won_on_sold", e.target.checked)} />Mark Sold as Won</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!cfg.lost_on_not_sold} onChange={(e) => set("lost_on_not_sold", e.target.checked)} />Mark Not sold as Lost</label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => save(true)} disabled={saving || !cfg.pipeline_id} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="ghl-sync-save">
              {saving ? "Saving…" : cfg.enabled ? "Save" : "Save & switch on"}
            </Button>
            {cfg.enabled && <Button variant="outline" onClick={() => save(false)} disabled={saving} className="rounded-full border-[#262626]">Switch off</Button>}
            <Button variant="outline" onClick={check} disabled={checking || !cfg.pipeline_id} className="rounded-full border-[#262626]" data-testid="ghl-sync-check">
              {checking ? "Checking…" : "Check matches (moves nothing)"}
            </Button>
          </div>
          {preview && (
            <div className="rounded-lg border border-[#262626] p-3 text-sm" data-testid="ghl-sync-preview">
              <div className="text-zinc-300 mb-2">Found <b>{preview.matched}</b> of the last {preview.checked} sold / not-sold consults in this pipeline:</div>
              <ul className="space-y-1 text-xs">
                {preview.rows.map((r, i) => (
                  <li key={i} className="flex flex-wrap gap-x-2">
                    <span className="text-zinc-200">{r.name}</span>
                    <span className="text-zinc-500">{r.date} · {r.outcome.replace(/_/g, " ")}</span>
                    {r.found
                      ? <span className="text-emerald-300">→ found, now in “{r.current_stage}”{r.status && r.status !== "open" ? ` (${r.status})` : ""}</span>
                      : <span className="text-amber-300">→ not found ({r.why})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}
